import { useEffect, useMemo, useRef, useState } from 'react';
import { buildPlanModel, type DayColumn, type SiteTile } from '../schichtplan/planModel';
import type { Site } from '../../lib/types';
import { EmptyState } from '../../components/ui/Field';
import { usePlan } from '../../lib/queries';
import {
  formatDateShort,
  formatRange,
  formatWeekdayLong,
  monthRange,
  shiftRange,
  todayIso,
  weekRange,
  type DateRange,
  type RangeMode,
} from '../../lib/dates';
import { ABSENCE_COLOR, ABSENCE_LABELS, ABSENCE_SHORT, absenceReason } from '../../lib/labels';
import { useToast } from '../../components/ui/Toast';
import './vorschau.css';

/**
 * Die Vorschau: derselbe Plan wie im Reiter „Schichtplan", aber nur zum
 * Ansehen — am Bildschirm im Büro oder auf dem Handy draußen. Es gibt hier
 * bewusst keinen einzigen Weg, etwas zu verändern: keine Knöpfe, kein Ziehen,
 * keine Dialoge. Wer ändern will, wechselt in den Schichtplan.
 */
export function VorschauView() {
  const [mode, setMode] = useState<RangeMode>('woche');
  const [range, setRange] = useState<DateRange>(() => weekRange(todayIso()));
  const [pdfBusy, setPdfBusy] = useState(false);
  const toast = useToast();

  // Die Woche zeigt Mo–So: die festen Bereiche (Störungsdienst, Lager, Büro)
  // laufen durchgehend bis Sonntag, deshalb gehören die Wochenenden ins Bild.
  const plan = usePlan(range);
  const model = useMemo(() => (plan.data ? buildPlanModel(plan.data, false) : null), [plan.data]);

  const changeMode = (next: RangeMode) => {
    if (next === 'frei') return;
    setMode(next);
    setRange(next === 'woche' ? weekRange(range.from) : monthRange(range.from));
  };

  const jumpToToday = () => {
    const today = todayIso();
    setRange(mode === 'monat' ? monthRange(today) : weekRange(today));
  };

  // Der PDF-Baustein (samt jspdf) wird erst beim Klick nachgeladen — wer nur
  // schaut, lädt ihn nie. Gezeichnet wird dasselbe Modell wie am Bildschirm.
  const downloadPdf = async () => {
    if (!model) return;
    setPdfBusy(true);
    try {
      const { exportPlanPdf } = await import('./planPdf');
      exportPlanPdf(model.columns, range, mode);
    } catch {
      toast.error('PDF konnte nicht erstellt werden. Bitte noch einmal versuchen.');
    } finally {
      setPdfBusy(false);
    }
  };

  if (plan.isLoading || !model || !plan.data) {
    return (
      <div className="loading">
        <span className="spinner" aria-hidden="true" />
        Vorschau wird geladen …
      </div>
    );
  }

  if (plan.data.employees.length === 0) {
    return (
      <div className="view">
        <div className="card">
          <EmptyState
            title="Noch nichts zu zeigen"
            description="Sobald Mitarbeiter und Baustellen angelegt sind, zeigt die Vorschau hier den Plan."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="view-full">
      <header className="vs-toolbar no-print">
        <div className="segmented" role="group" aria-label="Zeitraum-Art">
          <button type="button" aria-pressed={mode === 'woche'} onClick={() => changeMode('woche')}>
            Woche
          </button>
          <button type="button" aria-pressed={mode === 'monat'} onClick={() => changeMode('monat')}>
            Monat
          </button>
        </div>

        <div className="plan-stepper">
          <button
            type="button"
            className="btn btn-icon"
            onClick={() => setRange(shiftRange(range, mode, -1))}
            aria-label="Zurück"
          >
            ‹
          </button>
          <button type="button" className="btn" onClick={jumpToToday}>
            Heute
          </button>
          <button
            type="button"
            className="btn btn-icon"
            onClick={() => setRange(shiftRange(range, mode, 1))}
            aria-label="Weiter"
          >
            ›
          </button>
        </div>

        <span className="vs-range-label">{formatRange(range, mode)}</span>

        <div className="spacer" />

        <button
          type="button"
          className="btn"
          onClick={downloadPdf}
          disabled={pdfBusy}
          title="Den angezeigten Zeitraum als PDF herunterladen (DIN A4 quer, zum Ausdrucken)"
        >
          {pdfBusy ? 'PDF wird erstellt …' : 'Als PDF herunterladen'}
        </button>

        <span className="vs-readonly-badge" title="In der Vorschau kann nichts verändert werden">
          Nur Ansicht
        </span>
      </header>

      <VorschauBoard model={{ columns: model.columns }} />

      <footer className="vs-legend no-print">
        {(Object.keys(ABSENCE_LABELS) as (keyof typeof ABSENCE_LABELS)[]).map((type) => (
          <span key={type} className="vs-legend-item">
            <span className="swatch" style={{ background: ABSENCE_COLOR[type] }} />
            {ABSENCE_LABELS[type]}
          </span>
        ))}
      </footer>
    </div>
  );
}

function VorschauBoard({ model }: { model: { columns: DayColumn[] } }) {
  const today = todayIso();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Wie im Schichtplan: beim Öffnen zum heutigen Tag rollen — auf dem Handy
  // passt nur ein Tag ins Bild, und der interessiert am meisten.
  const datesKey = model.columns.map((column) => column.date).join('|');
  useEffect(() => {
    const box = scrollRef.current;
    if (!box || box.scrollWidth <= box.clientWidth) return;
    const todayColumn = box.querySelector<HTMLElement>('.vs-day.is-today');
    if (todayColumn) box.scrollLeft = todayColumn.offsetLeft;
    // eslint-disable-next-line react-hooks/exhaustive-deps — nur bei neuem Zeitraum
  }, [datesKey]);

  // Die festen Bereiche stehen an jedem Tag — als durchgehende Zeilen über
  // dem Brett. Ein gemeinsames Raster hält die Zeilenhöhen über alle Tage
  // hinweg gleich, erst darunter beginnen die gewöhnlichen Tageskacheln.
  const fixedSites: Site[] = [];
  const seenFixed = new Set<number>();
  for (const column of model.columns) {
    for (const tile of column.tiles) {
      if (tile.site.permanent && !seenFixed.has(tile.site.id)) {
        seenFixed.add(tile.site.id);
        fixedSites.push(tile.site);
      }
    }
  }

  const dayClasses = (column: DayColumn) =>
    classes(
      column.weekend && 'is-weekend',
      column.holiday && 'is-holiday',
      column.date === today && 'is-today',
    );

  return (
    <div className="vs-scroll" ref={scrollRef}>
      <div className="vs-canvas" style={{ '--cols': model.columns.length } as never}>
        <div className="vs-head-row" aria-hidden="true">
          {model.columns.map((column) => (
            <div key={column.date} className={classes('vs-head-cell', dayClasses(column))}>
              <span className="vs-day-weekday">{formatWeekdayLong(column.date)}</span>
              <span className="vs-day-date numeric">{formatDateShort(column.date)}</span>
              {column.holiday ? <span className="vs-day-holiday">{column.holiday}</span> : null}
            </div>
          ))}
        </div>

        {fixedSites.length > 0 ? (
          <div className="vs-fixed-band" role="list" aria-label="Feste Bereiche">
            {fixedSites.map((site) =>
              model.columns.map((column) => (
                <FixedCell
                  key={`${site.id}|${column.date}`}
                  site={site}
                  tile={column.tiles.find((tile) => tile.site.id === site.id) ?? null}
                  className={dayClasses(column)}
                  date={column.date}
                />
              )),
            )}
          </div>
        ) : null}

        <div className="vs-board" role="list" aria-label="Schichtplan-Vorschau nach Tagen">
          {model.columns.map((column) => (
            <VorschauDay key={column.date} column={column} today={today} />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Ein fester Bereich an einem Tag — eine Zelle der durchgehenden Zeile. */
function FixedCell({
  site,
  tile,
  className,
  date,
}: {
  site: Site;
  tile: SiteTile | null;
  className: string;
  date: string;
}) {
  const entries = tile?.entries ?? [];

  return (
    <div
      className={classes('vs-fixed-cell', className)}
      style={{ '--site': site.color } as never}
      role="listitem"
      aria-label={`${site.name}, ${formatWeekdayLong(date)} — ${
        entries.length > 0
          ? entries.map((entry) => entry.employee.displayName).join(', ')
          : 'niemand eingeteilt'
      }`}
    >
      <span className="vs-fixed-name">
        {site.name}
        {entries.length > 0 ? <em className="numeric">{entries.length}</em> : null}
      </span>
      {tile?.note ? <p className="vs-fixed-note">{tile.note}</p> : null}
      {entries.length > 0 ? (
        <ul className="vs-names">
          {entries.map((entry) => (
            <li
              key={entry.assignment.id}
              className={classes('vs-name', entry.conflict && 'has-conflict')}
              title={
                entry.conflict
                  ? `${entry.employee.displayName} — eingeteilt, obwohl nicht verfügbar: ${absenceReason(entry.conflict)}`
                  : undefined
              }
            >
              {entry.conflict ? (
                <span className="plan-conflict" aria-hidden="true">
                  !
                </span>
              ) : null}
              {entry.employee.displayName}
            </li>
          ))}
        </ul>
      ) : (
        <span className="vs-fixed-empty">–</span>
      )}
    </div>
  );
}

function VorschauDay({ column, today }: { column: DayColumn; today: string }) {
  // Die festen Bereiche stehen schon in der durchgehenden Zeile darüber.
  const tiles = column.tiles.filter((tile) => !tile.site.permanent);

  return (
    <section
      className={classes(
        'vs-day',
        column.weekend && 'is-weekend',
        column.holiday && 'is-holiday',
        column.date === today && 'is-today',
      )}
      role="listitem"
      aria-label={`${formatWeekdayLong(column.date)}, ${formatDateShort(column.date)}`}
    >
      <div className="vs-day-body">
        {tiles.map((tile) => (
          <VorschauTile key={tile.site.id} tile={tile} />
        ))}

        {tiles.length === 0 ? <p className="vs-day-empty">Keine Baustelle eingeplant.</p> : null}

        {column.absent.length > 0 ? (
          <div className="vs-rest">
            <h4 className="vs-rest-head">Abwesend</h4>
            <ul className="vs-names">
              {column.absent.map(({ employee, absence }) => (
                <li
                  key={employee.id}
                  className="vs-name is-absent"
                  style={{ '--abs': ABSENCE_COLOR[absence.type] } as never}
                  title={`${employee.displayName} — ${absenceReason(absence)}`}
                >
                  {employee.displayName}
                  <em>{ABSENCE_SHORT[absence.type]}</em>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {column.unplanned.length > 0 ? (
          <div className="vs-rest">
            <h4 className="vs-rest-head">Nicht eingeteilt</h4>
            <ul className="vs-names is-muted">
              {column.unplanned.map((employee) => (
                <li key={employee.id} className="vs-name is-open">
                  {employee.displayName}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/** Eine Baustelle an einem Tag — nur zum Lesen, mit den Namen als Liste. */
function VorschauTile({ tile }: { tile: SiteTile }) {
  // Alte Datenbestaende koennen NULL statt '' tragen, deshalb defensiv.
  const note = (tile.site.notes ?? '').trim();

  return (
    <article
      className="vs-tile"
      style={{ '--site': tile.site.color } as never}
      aria-label={tile.site.name}
    >
      <header className="vs-tile-head">
        <span className="vs-tile-name">{tile.site.name}</span>
        <span className="vs-tile-count numeric">{tile.entries.length}</span>
      </header>

      {tile.note ? <p className="vs-tile-note">{tile.note}</p> : null}

      {note ? <p className="vs-tile-note">{note}</p> : null}

      {tile.entries.length > 0 ? (
        <ul className="vs-names">
          {tile.entries.map((entry) => (
            <li
              key={entry.assignment.id}
              className={classes('vs-name', entry.conflict && 'has-conflict')}
              title={
                entry.conflict
                  ? `${entry.employee.displayName} — eingeteilt, obwohl nicht verfügbar: ${absenceReason(entry.conflict)}`
                  : undefined
              }
            >
              {entry.conflict ? (
                <span className="plan-conflict" aria-hidden="true">
                  !
                </span>
              ) : null}
              {entry.employee.displayName}
            </li>
          ))}
        </ul>
      ) : (
        <p className="vs-tile-hint">niemand eingeteilt</p>
      )}
    </article>
  );
}

function classes(...values: (string | false | null | undefined)[]): string {
  return values.filter(Boolean).join(' ');
}
